import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Star,
  MapPin,
  Search,
  Filter,
  ArrowLeft,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type configurations
const SERVICE_CONFIGS = {
  'traditional-wear': {
    title: 'Traditional Wear Specialists',
    subtitle: 'Expert tailoring for sarees, uniforms, office wear, and ethnic outfits',
    color: 'from-orange-500 to-red-500',
    emoji: '🧵',
  },
  'laundry-dryclean': {
    title: 'Laundries & Drycleaners',
    subtitle: 'Professional laundry, dry cleaning, and garment care services',
    color: 'from-cyan-500 to-teal-500',
    emoji: '🧺',
  },
  'beauty-skincare': {
    title: 'Beauty Services',
    subtitle: 'Local beauty professionals for facials, makeup, and more',
    color: 'from-pink-500 to-rose-500',
    emoji: '💄',
  },
  'jewellery-watches': {
    title: 'Jewellery & Watch Services',
    subtitle: 'Certified repair, resizing, and authentication',
    color: 'from-yellow-500 to-amber-500',
    emoji: '💍',
  },
  'bags-accessories': {
    title: 'Bag Repair & Restoration',
    subtitle: 'Luxury bag care, repair, and authentication services',
    color: 'from-amber-500 to-orange-500',
    emoji: '👜',
  },
  'shoes-footwear': {
    title: 'Shoe Repair & Care',
    subtitle: 'Expert shoe repair, cleaning, and restoration services',
    color: 'from-gray-600 to-slate-600',
    emoji: '👟',
  },
  'alterations': {
    title: 'Tailoring & Alterations',
    subtitle: 'Find expert tailors for alterations, custom fittings, and repairs',
    color: 'from-purple-600 to-pink-600',
    emoji: '👗',
  },
  'perfumes-cosmetics': {
    title: 'Fragrance & Beauty Consultation',
    subtitle: 'Premium scent profiling and makeup consultation',
    color: 'from-purple-400 to-pink-400',
    emoji: '🌸',
  },
};

const ServiceListingPage = () => {
  const { formatCurrency } = useCountry();
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');

  const config = SERVICE_CONFIGS[category] || {
    title: 'Service Providers',
    subtitle: 'Find trusted service providers',
    color: 'from-gray-500 to-gray-600',
    emoji: '🔧',
  };

  useEffect(() => {
    fetchProviders();
  }, [category]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/services/providers`, {
        params: {
          country: 'SL',
          service_type: category,
          limit: 50, // Get all providers
        },
      });
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort providers
  const filteredProviders = providers
    .filter(p => 
      p.provider_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.specializations?.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'reviews') return (b.review_count || 0) - (a.review_count || 0);
      if (sortBy === 'price_low') return (a.starting_price || 0) - (b.starting_price || 0);
      if (sortBy === 'price_high') return (b.starting_price || 0) - (a.starting_price || 0);
      return 0;
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${config.color} text-white py-12`}>
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 mb-4"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <span className="text-5xl">{config.emoji}</span>
            {config.title}
          </h1>
          <p className="text-xl opacity-90">{config.subtitle}</p>
          <p className="mt-2 opacity-75">{filteredProviders.length} service providers available</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search and Filter Bar */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name or specialization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Highest Rated</SelectItem>
              <SelectItem value="reviews">Most Reviews</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Provider Grid */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-orange-500" />
            <p className="mt-4 text-gray-500">Loading service providers...</p>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No service providers found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProviders.map((provider) => (
              <Card key={provider.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {provider.is_sponsored && (
                          <Badge className="bg-orange-100 text-orange-700 text-xs">
                            Sponsored
                          </Badge>
                        )}
                        {provider.verification_level >= 2 && (
                          <Badge variant="outline" className="text-xs">
                            Verified
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-lg font-bold">{provider.provider_name}</h3>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                        <span className="font-bold">{provider.rating?.toFixed(1)}</span>
                      </div>
                      <p className="text-sm text-gray-500">({provider.review_count})</p>
                    </div>
                  </div>

                  {/* Specializations */}
                  <p className="text-sm text-gray-600 mb-3">
                    {provider.specializations?.slice(0, 3).join(' · ')}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {provider.achievements?.slice(0, 3).map((achievement, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {achievement.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>

                  {/* Location */}
                  {provider.city && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                      <MapPin className="h-4 w-4" />
                      {provider.city}
                    </div>
                  )}

                  {/* Pricing */}
                  {provider.fixed_price_services?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-xs text-gray-500 mb-2">Sample Services</p>
                      {provider.fixed_price_services.slice(0, 2).map((service, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600">{service.name}</span>
                          <span className="font-medium">{formatCurrency(service.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Starting from</p>
                      <p className="text-xl font-bold text-orange-600">
                        {formatCurrency(provider.starting_price)}
                      </p>
                    </div>
                    <Button className={`bg-gradient-to-r ${config.color}`}>
                      Get Quote
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceListingPage;
